import { useMemo, useState } from "react";
import { Check, X, Plus, Search } from "lucide-react";

/**
 * Reusable multi-select chip picker used in tutor + lead forms.
 *
 * Props
 * -----
 *   options       Array<{ value: string, label?: string, hint?: string, isPrimary?: boolean }>
 *   value         Array<string>   — currently selected values
 *   onChange      (next: string[]) => void
 *   placeholder   Search box hint
 *   allowCustom   When true, lets the user add free-text values not in `options`
 *   max           Optional cap (e.g. 12 grades)
 *   className     Outer class override
 *   testid        Base test-id, e.g. "tutor-subjects"
 *   pillTone      Tailwind class set for the selected pill (default emerald)
 */
export default function MultiSelectChips({
    options = [],
    value = [],
    onChange,
    placeholder = "Search…",
    allowCustom = false,
    max,
    className = "",
    testid = "msc",
    pillTone = "bg-emerald-100 border-emerald-400 text-emerald-900",
}) {
    const [q, setQ] = useState("");
    const set = useMemo(() => new Set(value), [value]);

    const filtered = useMemo(() => {
        const qq = q.trim().toLowerCase();
        if (!qq) return options;
        return options.filter((o) => (o.label || o.value).toLowerCase().includes(qq));
    }, [options, q]);

    const exactMatch = useMemo(() => options.some((o) => (o.value || "").toLowerCase() === q.trim().toLowerCase()), [options, q]);

    const toggle = (val) => {
        if (!val) return;
        const next = new Set(set);
        if (next.has(val)) { next.delete(val); }
        else {
            if (max && next.size >= max) return;
            next.add(val);
        }
        onChange?.(Array.from(next));
    };

    const addCustom = () => {
        const val = q.trim();
        if (!val) return;
        if (max && set.size >= max) return;
        if (set.has(val)) { setQ(""); return; }
        onChange?.([...value, val]);
        setQ("");
    };

    return (
        <div className={`space-y-2 ${className}`} data-testid={`${testid}-root`}>
            {/* Selected pills */}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-selected`}>
                    {value.map((v) => (
                        <span
                            key={v}
                            data-testid={`${testid}-pill-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold ${pillTone}`}
                        >
                            {v}
                            <button
                                type="button"
                                onClick={() => toggle(v)}
                                aria-label={`Remove ${v}`}
                                data-testid={`${testid}-remove-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="opacity-70 hover:opacity-100"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Search box */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && allowCustom && !exactMatch) {
                            e.preventDefault();
                            addCustom();
                        }
                    }}
                    placeholder={placeholder}
                    data-testid={`${testid}-search`}
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                />
                {allowCustom && q.trim() && !exactMatch && (
                    <button
                        type="button"
                        onClick={addCustom}
                        data-testid={`${testid}-add-custom`}
                        className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-[#1E3A8A] text-white font-semibold"
                    >
                        <Plus className="w-3 h-3" /> Add &ldquo;{q.trim()}&rdquo;
                    </button>
                )}
            </div>

            {/* Options grid */}
            <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto p-1 -m-1" data-testid={`${testid}-options`}>
                {filtered.length === 0 && (
                    <div className="text-xs text-slate-400 px-1.5 py-1">No matches{allowCustom ? " — press Enter or click Add to create" : ""}.</div>
                )}
                {filtered.map((o) => {
                    const v = o.value;
                    const on = set.has(v);
                    return (
                        <button
                            key={v}
                            type="button"
                            onClick={() => toggle(v)}
                            data-testid={`${testid}-opt-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            data-on={on}
                            data-primary={o.isPrimary ? "true" : undefined}
                            disabled={!on && max && set.size >= max}
                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition ${
                                on
                                    ? `${pillTone} font-semibold`
                                    : "bg-white border-slate-200 text-slate-600 hover:border-emerald-300"
                            } ${!on && max && set.size >= max ? "opacity-40 cursor-not-allowed" : ""}`}
                            title={o.hint || (o.isPrimary ? "Primary-level grade — eligible for All Major Subjects" : "")}
                        >
                            {on && <Check className="w-3 h-3" />}
                            {o.label || v}
                            {o.isPrimary && !on && <span className="text-emerald-500/70 text-[10px]">★</span>}
                        </button>
                    );
                })}
            </div>
            {max && (
                <div className="text-[10px] text-slate-400 text-right">
                    {value.length}/{max} selected
                </div>
            )}
        </div>
    );
}
